/**
 * Los DOS dirties de la pantalla, independientes a propósito (regla del mockup: guardar la
 * plantilla nunca limpia el estado del informe, y viceversa).
 *
 * Modelado sobre `DataExplorerPage._canonicalizeConfig` y conservando sus decisiones:
 *  - `selected_items` se ORDENA (el orden de selección no es semántico);
 *  - el orden de `fields` NO se ordena: es el orden de la tabla y reordenar cuenta como cambio;
 *  - `compare_period` sólo entra si `compare` está prendido;
 *  - lo que el backend pisa post-query (chart_metrics / chart_time_period) queda AFUERA para no
 *    producir falsos positivos.
 */
import {serializeLayout, withoutTextContent} from './informeModel';

export function canonicalSettings(settings) {
    const s = settings || {};
    return {
        source_type: s.source_type || null,
        selected_items: [...(s.selected_items || [])].map(String).sort(),
        period: s.period || null,
        compare: !!s.compare,
        compare_period: s.compare ? (s.compare_period || null) : null,
        dimensions_filters: s.dimensions_filters || [],
    };
}

/**
 * Un panel sin opciones y uno con `options: {}` son el mismo panel: el backend normaliza la ausencia
 * a `{}`, así que uno guardado vuelve con `{}` y uno nacido en el editor no las trae. Sin esto, prender
 * y apagar «Ocultar filas sin datos» dejaba el informe «Sin guardar» (review C-tabla-02). Sólo para
 * COMPARAR: lo que se guarda (`serializeLayout`) no cambia.
 */
function withoutEmptyOptions(panel) {
    if (!panel || !panel.options || Object.keys(panel.options).length) return panel;
    const {options, ...rest} = panel;
    return rest;
}

/**
 * `withText: false` es la mirada de la PLANTILLA: una plantilla guarda los bloques de texto vacíos
 * (`withoutTextContent`, decisión de fede del 24/10), así que escribir los insights de este cliente
 * no la modifica. Sin esto, escribir un texto ofrecía «Actualizar también la plantilla» y,
 * después de guardarla, la hoja quedaba distinta de su plantilla para siempre. El INFORME sí los
 * cuenta: lo escrito es trabajo sin guardar.
 */
export function canonicalTemplate(template, {withText = true} = {}) {
    const serialized = serializeLayout(template || {fields: [], panels: []});
    const layout = withText ? serialized : withoutTextContent(serialized);
    return {
        fields: (template && template.fields ? template.fields : []).map((f) => `${f.kind}:${f.name}`),
        panels: layout.panels.map(withoutEmptyOptions),
    };
}

export function canonicalizeTemplate(state) {
    return JSON.stringify(canonicalTemplate(state.template, {withText: false}));
}

export function canonicalizeInforme(state) {
    return JSON.stringify({
        name: state.informe ? state.informe.name : null,
        mode: state.informe ? state.informe.mode : null,
        settings: canonicalSettings(state.settings),
        template: canonicalTemplate(state.template),
    });
}

/**
 * Las formas canónicas se piden VARIAS veces por cambio de estado —los cuatro derivados del
 * `InformeContext` las consultan— y cada una es un `JSON.stringify` de la hoja entera. El reducer es
 * PURO (cada dispatch devuelve un objeto nuevo y nadie muta el anterior), así que cachearlas por
 * IDENTIDAD del estado es seguro: la entrada no puede quedar vieja, y muere con el estado que la
 * produjo porque el `WeakMap` no lo retiene (review C-informes-07).
 *
 * Sólo lo usan los predicados de acá. `canonicalizeInforme` / `canonicalizeTemplate` siguen
 * calculando directo: el reducer las llama sobre estados que está construyendo, para ESTAMPAR un
 * baseline, y ahí el cacheo no aporta.
 */
const canonCache = new WeakMap();

function canonOf(state) {
    let entry = canonCache.get(state);
    if (!entry) {
        entry = {informe: canonicalizeInforme(state), template: canonicalizeTemplate(state)};
        canonCache.set(state, entry);
    }
    return entry;
}

/** Sin informe cargado siempre es "Sin guardar". */
export function isInformeDirty(state) {
    if (!state.informe || !state.baseline || !state.baseline.informe) return true;
    return canonOf(state).informe !== state.baseline.informe;
}

/**
 * La HOJA (campos y paneles) cambió respecto de su baseline, HAYA O NO una plantilla elegida.
 *
 * `isTemplateModified` no sirve para esta pregunta: exige `template.id`, así que en el caso más
 * común del arreglo —informe nuevo con paneles agregados— da `false`, y el modal terminaba listando
 * «El informe (nombre, ajustes o modo)» cuando lo que se perdía eran los paneles (review
 * C-informes-06).
 */
export function isSheetModified(state) {
    if (!state || !state.baseline || state.baseline.template == null) return false;
    return canonOf(state).template !== state.baseline.template;
}

/** Sólo tiene sentido con una plantilla elegida: sin plantilla no hay nada que "actualizar". */
export function isTemplateModified(state) {
    if (!state.template || !state.template.id || !state.baseline || !state.baseline.template) return false;
    return canonOf(state).template !== state.baseline.template;
}

/**
 * ¿Hay trabajo del usuario que se perdería al abrir otro informe (o al recargar la pestaña)?
 *
 * NO es `isInformeDirty`: un informe nuevo nunca estuvo guardado —el header dice «Sin guardar» y
 * está bien—, pero recién nacido no tiene NADA que perder. Preguntar ahí hacía saltar el aviso en
 * una pantalla que el usuario no había tocado, y un aviso que aparece sin motivo entrena a
 * ignorarlos (card eo7J0qds).
 *
 * El baseline del informe es el punto de referencia del que el usuario no se movió: lo fijan
 * guardar, cargar, NACER (`NEW_INFORME`) y cambiar de cliente. Sin baseline no hay contra qué
 * comparar y tampoco nada que perder.
 */
export function hasUnsavedWork(state) {
    if (isTemplateModified(state)) return true;
    if (!state || !state.baseline || state.baseline.informe == null) return false;
    return canonOf(state).informe !== state.baseline.informe;
}
